import React, {useEffect, useState} from 'react';
import {
    AddButton,
    ColumnOne,
    ColumnTwo,
    DetailedFiltersContent,
    DropdownWrapper,
    ResetButton
} from './FileTypePickerAdvanced.style';
import {OptionProps} from '@bosch/react-frok';
import {t} from 'i18next';
import {FilterCategories} from '../SearchBar/SearchBar.style';
import {mainFileTypeResource} from './resources/MainFileTypeResource';
import {dynamicConfig} from '../../init/config';
import {isNullOrEmpty} from '../../utils';
import {FileTypePicker} from './FileTypePicker';
import {FileType, getFileTypeResource} from './FileTypeResourceRegister';
import {fileTypeMainToSubResource} from './resources/FileTypeMainToSubResource';
import {
    getEmptyFileTypeOptionProp,
    getLocalizedBlankFileTypeOptionPropName,
    getLocalizedFileTypeNameToOptionPropName,
    SELECT_TYPE
} from './resources/FileTypeResource';

export interface FileTypePickerAdvancedProps {
    readonly resetState: boolean;
    readonly resetInput: boolean;
    readonly fileTypes: string[];
    onChange: (fileTypeSelection: string[]) => void;
}

const getMainFileTypeToSubTypeMap = (): Map<string, string[]> => {
    let mappedSubFilterTypes: string[] = [];
    let mainFileTypeToSubTypeScope = new Map<string, string[]>();
    fileTypeMainToSubResource
        .filter(resourceTypeMap => ((isNullOrEmpty(resourceTypeMap.subType)) || (dynamicConfig.SUB_FILTERS.includes(resourceTypeMap.subType))))
        .map(resourceTypeMap => {
            if (!mappedSubFilterTypes.includes(resourceTypeMap.subType)) {
                mappedSubFilterTypes.push(resourceTypeMap.subType);
            }

            let subTypesTypes: string[] = mainFileTypeToSubTypeScope.get(resourceTypeMap.mainType) ?? [];
            if (subTypesTypes && (subTypesTypes.length > 0)) {
                mainFileTypeToSubTypeScope.set(resourceTypeMap.mainType, Array.from(new Set([...subTypesTypes, resourceTypeMap.subType])));
            } else {
                mainFileTypeToSubTypeScope.set(resourceTypeMap.mainType, [SELECT_TYPE, resourceTypeMap.subType]);
            }
        })

    mainFileTypeResource
        .filter(type => (!Array.from(mainFileTypeToSubTypeScope.keys()).includes(type.name)))
        .filter(type => ((isNullOrEmpty(type.value)) || (dynamicConfig.MAIN_FILTERS.includes(type.value))))
        .map(type => {
            mainFileTypeToSubTypeScope.set(type.name,
                Array.from(new Set(fileTypeMainToSubResource
                    .filter(resourceTypeMap => (type.name === resourceTypeMap.mainType))
                    .map(resourceTypeMap => resourceTypeMap.subType))));
        });

    let otherSubTypes = dynamicConfig.SUB_FILTERS.filter(type => (!mappedSubFilterTypes.includes(type)));
    if (otherSubTypes && (otherSubTypes.length > 0)) {
        let mappedOtherSubTypes = mainFileTypeToSubTypeScope.get(FileType.OTHER);
        if (mappedOtherSubTypes && (mappedOtherSubTypes.length > 0)) {
            otherSubTypes = [...mappedOtherSubTypes, ...otherSubTypes];
        }

        mainFileTypeToSubTypeScope.set(FileType.OTHER, Array.from(new Set(otherSubTypes)));
    }

    return mainFileTypeToSubTypeScope;
}

const getMainFileTypeOptions = (mainFileTypeToSubTypeScope: Map<string, string[]>): OptionProps[] => {
    let options: OptionProps[] = [];
    mainFileTypeResource
        .filter(type => ((Array.from(mainFileTypeToSubTypeScope.keys()).includes(type.name))
            && (!Array.from(mainFileTypeToSubTypeScope.keys()).includes(SELECT_TYPE))))
        .map(type => {
            options.push({name: getLocalizedFileTypeNameToOptionPropName(type.name), value: type.value});
        });

    return Array.from(new Set(options));
};

const getSubFileTypeOptions = (mainFileTypeSelectionState: string, mainFileTypeToSubTypeScope: Map<string, string[]>): OptionProps[] => {
    let options: OptionProps[] = [];
    options.push(getEmptyFileTypeOptionProp());
    if (isNullOrEmpty(mainFileTypeSelectionState)) {
        return options;
    }

    let resourceType = getFileTypeResource(mainFileTypeSelectionState);
    if (!resourceType) {
        return options;
    }

    let subTypes = mainFileTypeToSubTypeScope.get(mainFileTypeSelectionState) ?? [];
    if ((!subTypes) || (subTypes.length === 0)) {
        return options;
    }

    resourceType
        .filter(type => ((subTypes.includes(type.name))
            && (!type.name.includes(SELECT_TYPE))))
        .map(type => options.push({
            // name: getLocalizedFileTypeNameToOptionPropName(type.name),
            name: type.name,
            value: type.value
        }));

    if (FileType.OTHER === mainFileTypeSelectionState) {
        subTypes.map(type => {
            options.push({
                // name: getLocalizedFileTypeNameToOptionPropName(type.type),
                name: type,
                value: type
            })
        });
    }

    return Array.from(new Set(options));
};

/**
 * @name FileTypePickerAdvanced
 * @param {string[]} props.fileTypes - An array of strings representing the file types that the user can filter by.
 * @param {(fileTypeSelection: string) => void} props.onChange - A function that is called when a file is selected.
 *
 * @description
 * This component is responsible for rendering the detailed filter picker in the application.
 * It allows users to select main and sub filters for file types. The available filters are dynamically
 * loaded from a configuration. The selected filters are then communicated back to the parent component
 * through a callback function. This component maintains its own state for the currently selected main
 * and sub filters.
 */
export const FileTypePickerAdvanced: React.VFC<FileTypePickerAdvancedProps> = (props) => {
    const [mainFileTypeToSubTypeScope] = useState<Map<string, string[]>>(getMainFileTypeToSubTypeMap());
    const [fileSubTypeSelection, setFileSubTypeSelection] = useState<string[]>(props.fileTypes ?? []);
    const [mainFileTypeInput, setMainFileTypeInput] = useState<string>('');
    const [subFileTypeOptions, setSubFileTypeOptions] = useState<OptionProps[]>(getSubFileTypeOptions(mainFileTypeInput, mainFileTypeToSubTypeScope));
    const [mainFileTypeOptions] = useState<OptionProps[]>(getMainFileTypeOptions(mainFileTypeToSubTypeScope));
    const [mainFileTypeOptionsUsed, setMainFileTypeOptionsUsed] = useState<string[]>([]);
    const [isResetMainFileType, setIsResetMainFileType] = useState<boolean>(false);
    const [isResetSubFileType, setIsResetSubFileType] = useState<boolean>(false);

    const getFilteredMainFileTypeOptions = (): OptionProps[] => {
        if ((!mainFileTypeOptions) || (mainFileTypeOptions.length === 0)) {
            return [];
        }

        let options: OptionProps[] = [];
        options.push(getEmptyFileTypeOptionProp());
        mainFileTypeOptions
            .filter(type => (!mainFileTypeOptionsUsed.includes('' + (type.value ?? ''))))
            .map(type => options.push(type));

        return Array.from(new Set(options));
    }

    const getFilteredSubTypeOptions = (selectionOptions: OptionProps[]): OptionProps[] => {
        let options: OptionProps[] = [];
        if ((!selectionOptions) || (selectionOptions.length === 0)) {
            options.push(getEmptyFileTypeOptionProp());
            return options;
        }
        selectionOptions.map(type => options.push(type));

        return Array.from(new Set(options));
    }

    const setState = (selectionState: string[], selectionOptions: OptionProps[], resetInput: boolean) => {
        selectionState = Array.from(new Set(selectionState.filter(type => (!isNullOrEmpty(type)))));
        setFileSubTypeSelection(selectionState);
        setSubFileTypeOptions(getFilteredSubTypeOptions(selectionOptions));
        if ((selectionOptions.length === 1) && (selectionOptions[0].name === getLocalizedBlankFileTypeOptionPropName())) {
            setMainFileTypeInput('');
        }

        if (!isNullOrEmpty(mainFileTypeInput)) {
            props.onChange(selectionState);
        }

        if (resetInput) {
            if ((!isNullOrEmpty(mainFileTypeInput)) && (fileSubTypeSelection.length > 0)) {
                setMainFileTypeInput('');
                setIsResetMainFileType(true);
            }

            setMainFileTypeOptionsUsed([]);
            setIsResetSubFileType(true);
        }
    };

    useEffect(() => {
        let activeMainFileTypes: string[] = [];
        mainFileTypeToSubTypeScope.forEach((values, keys) => {
            let selectedTypes = [...props.fileTypes, SELECT_TYPE];
            let result = values.filter(value => (!selectedTypes.includes(value)));
            if (result.length > 0) {
                activeMainFileTypes.push(keys);
            }
        })

        if (mainFileTypeOptionsUsed.includes(mainFileTypeInput)) {
            setMainFileTypeInput('');
            setIsResetMainFileType(false);
            setIsResetSubFileType(true);
        }

        setMainFileTypeOptionsUsed(mainFileTypeOptionsUsed.filter(type => (!activeMainFileTypes.includes(type))));
        let selectionOptions = getSubFileTypeOptions(mainFileTypeInput, mainFileTypeToSubTypeScope)
            .filter(type => (props.fileTypes && (!props.fileTypes.includes(type.name))));
        setSubFileTypeOptions(selectionOptions);
        setFileSubTypeSelection(props.fileTypes ?? []);
    }, [props.fileTypes]);

    useEffect(() => {
        if (props.resetState) {
            setState([], getSubFileTypeOptions('', mainFileTypeToSubTypeScope), true);
        }
    }, [props.resetState]);

    const handleMainFileTypeFilterInput = (fileTypeSelection: string) => {
        setMainFileTypeInput(fileTypeSelection);
        let subTypeOptions = [];
        if (fileTypeSelection !== getLocalizedBlankFileTypeOptionPropName()) {
            subTypeOptions = getSubFileTypeOptions(fileTypeSelection, mainFileTypeToSubTypeScope)
                .filter(type => (!fileSubTypeSelection.includes(type.name)));
            setSubFileTypeOptions(subTypeOptions);
        } else {
            subTypeOptions = getFilteredSubTypeOptions([]);
            setSubFileTypeOptions(subTypeOptions);
        }

        setIsResetMainFileType(false);
        setState(fileSubTypeSelection, subTypeOptions, false);
    };

    const handleMainFileTypeFilterAdd = () => {
        let selectionState = Array.from(new Set(fileSubTypeSelection.concat(subFileTypeOptions
            .filter(type => ((!isNullOrEmpty(type.name)) && (getLocalizedBlankFileTypeOptionPropName() !== type.name)))
            .map(type => type.name))));
        setMainFileTypeOptionsUsed([...mainFileTypeOptionsUsed, mainFileTypeInput]);
        setMainFileTypeInput('');
        setIsResetMainFileType(true);
        setIsResetSubFileType(true);
        setState(selectionState, [], props.resetInput);
    }

    const handleSubFileTypeFilterInput = (fileTypeSelection: string) => {
        handleSubFileTypeFilterAdd(fileTypeSelection);
    };

    const handleSubFileTypeFilterAdd = (fileTypeSelection: string) => {
        let selectionState = Array.from(new Set([...fileSubTypeSelection, fileTypeSelection].filter(type => (!isNullOrEmpty(type)))));
        let filteredSubLists = subFileTypeOptions.filter(type => (!selectionState.includes(type.name)));
        if ((filteredSubLists.length === 1) && (filteredSubLists[0].name === getLocalizedBlankFileTypeOptionPropName())) {
            setMainFileTypeOptionsUsed([...mainFileTypeOptionsUsed, mainFileTypeInput]);
            setMainFileTypeInput('');
            setIsResetMainFileType(true);
        }

        setIsResetSubFileType(true);
        setState(selectionState, filteredSubLists, props.resetInput);
    }

    const handleFileTypeFilterReset = () => {
        setState([], getSubFileTypeOptions('', mainFileTypeToSubTypeScope), true);
        setMainFileTypeOptionsUsed([]);
        setMainFileTypeInput('');
        setIsResetMainFileType(true);
        setIsResetSubFileType(true);
    }

    const isResetDataPicker = (): boolean => {
        return ((isNullOrEmpty(mainFileTypeInput) || (mainFileTypeInput === SELECT_TYPE))) as boolean;
    }

    const isSybTypeFileTypePickerActive = (): boolean => {
        return ((subFileTypeOptions === undefined) || (!subFileTypeOptions) || (subFileTypeOptions.length === 0)
            || ((subFileTypeOptions.length === 1) && (subFileTypeOptions[0].name === getLocalizedBlankFileTypeOptionPropName()))) as boolean;
    };

    let subTypeFilePickerDisabled = (isSybTypeFileTypePickerActive());

    let resetDataPicker: boolean = (isResetMainFileType && isResetDataPicker()) as boolean;

    let resetSubTypeFile: boolean = (isResetSubFileType || resetDataPicker) as boolean;

    const isVisibleResetButton = (): boolean => {
        return (fileSubTypeSelection && (fileSubTypeSelection.length > 0)) as boolean;
    }

    let visibleResetButton = isVisibleResetButton();

    return (
        <div>
            <DetailedFiltersContent>
                <ColumnOne>
                    <FilterCategories>
                        {t('FILTERS_AREA_FILE_TYPE', {count: props.fileTypes.length})}
                    </FilterCategories>
                </ColumnOne>
                <ColumnTwo>
                    <DropdownWrapper>
                        <FileTypePicker dataSource={getFilteredMainFileTypeOptions()}
                                        defaultValue={''}
                                        resetState={resetDataPicker}
                                        resetInput={resetDataPicker}
                                        disabled={false}
                                        onChange={(fileTypeSelection: string) => handleMainFileTypeFilterInput(fileTypeSelection)}
                        />
                        <AddButton
                            key='filterMainFileTypesAddButton'
                            icon='add'
                            mode='primary'
                            disabled={!mainFileTypeInput}
                            type='button'
                            label={t('FILTER_TYPE_COMPONENT_ADD_FILTER_LABEL')}
                            onClick={handleMainFileTypeFilterAdd}
                        />
                    </DropdownWrapper>
                    <DropdownWrapper>
                        <FileTypePicker dataSource={subFileTypeOptions}
                                        defaultValue={''}
                                        resetState={resetSubTypeFile}
                                        resetInput={resetSubTypeFile}
                                        disabled={subTypeFilePickerDisabled}
                                        onChange={(fileTypeSelection: string) => handleSubFileTypeFilterInput(fileTypeSelection)}
                        />
                        <ResetButton
                            key='filterFileTypesResetButton'
                            icon='reset'
                            mode='secondary'
                            disabled={!visibleResetButton}
                            type='button'
                            label={t('FILTER_TYPE_COMPONENT_RESET_FILTER_LABEL')}
                            onClick={handleFileTypeFilterReset}
                        />
                    </DropdownWrapper>
                </ColumnTwo>
            </DetailedFiltersContent>
        </div>
    );
};